import { lazy, Suspense } from 'react'

import { createFileRoute, Link, notFound } from '@tanstack/react-router'

import { SearchX } from 'lucide-react'

import { actionQueries } from '@/features/actions/queries'
import { AutomationSettingsFormSkeleton } from '@/features/air/automation-settings/components/skeletons'
import { automationQueries } from '@/features/air/automation-settings/queries'
import { policyQueries } from '@/features/air/policy-management/queries'
import { RouteErrorComponent } from '@/features/app-shell/route-error-component'
import { Section, SectionTitle } from '@/features/app-shell/section'
import { playbookQueries } from '@/features/playbooks/queries'

import { ROLES } from '@/shared/auth/constants'
import { getCurrentTenant } from '@/shared/auth/store'
import { roleAuthGuard } from '@/shared/auth/utils/router'

import BackLink from '@/components/common/back-link'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/components/ui/empty'

const AutomationSettingsForm = lazy(
	() => import('@/features/air/automation-settings/components/form'),
)

export const Route = createFileRoute(
	'/_dashboard/air/automation-settings/$mapping_id',
)({
	beforeLoad: () => roleAuthGuard(ROLES.Analyst),

	loader: async ({ context: { queryClient }, params }) => {
		const tenant_id = getCurrentTenant()

		if (tenant_id) {
			// Prefetch list of actions
			queryClient.prefetchQuery(actionQueries.list(tenant_id))

			// Prefetch list of playbooks
			queryClient.prefetchQuery(playbookQueries.list(tenant_id))

			const data = await queryClient.ensureQueryData({
				...automationQueries.automation_details(tenant_id, params.mapping_id),
				revalidateIfStale: true,
			})

			const automation = data.responses.find(
				(r) => r.mapping_id === decodeURIComponent(params.mapping_id),
			)

			// Deleting a custom policy cascades to its automations, so a bookmark or
			// a not-yet-invalidated table row can point at a mapping the API no
			// longer returns (`responses: []`). Everything below indexes into that
			// list, so bail here rather than render a page built from `undefined`.
			if (!automation) throw notFound()

			// Prefetch policy details for the mapped policy
			if (automation.mapped_id) {
				queryClient.prefetchQuery(
					policyQueries.policy_details(tenant_id, automation.mapped_id),
				)
			}

			return { automation }
		}
	},

	head: ({ loaderData }) => ({
		meta: [
			{
				title: `Edit: ${loaderData?.automation.response_name} | Air | BitLyft AIR®`,
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RoutePendingComponent,
	errorComponent: RouteErrorComponent,
	notFoundComponent: RouteNotFoundComponent,
})

function RouteComponent() {
	const { mapping_id } = Route.useParams()
	const data = Route.useLoaderData()

	if (!data) {
		return <RoutePendingComponent />
	}

	return (
		<Section>
			<div className="flex items-start justify-start gap-4">
				<BackLink to="/air/automation-settings" />

				<SectionTitle>{data.automation.response_name}</SectionTitle>
			</div>

			<div className="flex w-full flex-col items-start justify-start gap-2">
				<ErrorBoundary
					feature="air-automation"
					section="edit-form"
					entityId={mapping_id}
				>
					<Suspense fallback={<AutomationSettingsFormSkeleton />}>
						<AutomationSettingsForm mapping_id={mapping_id} />
					</Suspense>
				</ErrorBoundary>
			</div>
		</Section>
	)
}

/**
 * Reached when the mapping id in the URL is not in the automations list — the
 * usual cause is that the policy it was mapped to has been deleted, which
 * cascades to the automation. Names that cause so the user is not left guessing.
 */
function RouteNotFoundComponent() {
	return (
		<Section>
			<Empty className="border-border flex-none rounded-xl border border-dashed py-16">
				<EmptyHeader>
					<EmptyMedia variant="icon">
						<SearchX />
					</EmptyMedia>
					<EmptyTitle>Automation not found</EmptyTitle>
					<EmptyDescription>
						This automation no longer exists. It was most likely removed with
						the custom policy it was mapped to.
					</EmptyDescription>
				</EmptyHeader>

				<EmptyContent>
					<Button
						variant="outline"
						nativeButton={false}
						render={
							<Link to="/air/automation-settings" preload="intent">
								Back to automations
							</Link>
						}
					/>
				</EmptyContent>
			</Empty>
		</Section>
	)
}

function RoutePendingComponent() {
	return (
		<Section>
			<div className="flex w-full flex-col items-start justify-start gap-2">
				<AutomationSettingsFormSkeleton />
			</div>
		</Section>
	)
}
